'use client';

import React, { memo, useMemo, useState } from 'react';
import type { RunMeta } from '@/types';

type DegradationEntry = NonNullable<RunMeta['degradations']>[number];

const STAGE_LABELS: Record<string, string> = {
  policy_suite: 'Policy checks',
  evidence_repair: 'Evidence verification',
  research_search: 'Web research',
  rag_retrieval: 'Document retrieval',
  rag_embedding: 'Document indexing',
  reranker: 'Source ranking',
  writer: 'Drafting',
  crisis_draft: 'Crisis drafting',
  llm: 'Model output',
  render: 'Formatting',
};

function stageLabel(stage: string): string {
  return STAGE_LABELS[stage] || stage.replace(/_/g, ' ');
}

export const DegradationNotice = memo(({ degradations }: { degradations: DegradationEntry[] }) => {
  const [open, setOpen] = useState(false);
  const hasCritical = degradations.some((entry) => entry.severity === 'critical');
  const hasWarning = hasCritical || degradations.some((entry) => entry.severity === 'warning');

  const summary = useMemo(() => {
    if (hasCritical) return 'This response was generated with reduced safeguards.';
    if (hasWarning) return 'Some checks or sources were unavailable for this response.';
    return 'Minor fallbacks were used while generating this response.';
  }, [hasCritical, hasWarning]);

  if (!degradations.length) return null;

  const toneClasses = hasCritical
    ? 'border-destructive/40 bg-destructive/10'
    : hasWarning
      ? 'border-warning/40 bg-warning/10'
      : 'border-border/40 bg-muted/30';

  return (
    <div className={`mb-3 rounded-2xl border p-3 text-foreground ${toneClasses}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span>{summary}</span>
        <button
          type="button"
          className="text-xs underline-offset-4 hover:underline"
          onClick={() => setOpen((prev) => !prev)}
        >
          {open ? 'Hide details' : `View details (${degradations.length})`}
        </button>
      </div>
      {open && (
        <div className="mt-3 space-y-2 text-xs text-foreground">
          {degradations.map((entry, idx) => (
            <div
              key={`${entry.stage}-${entry.code}-${idx}`}
              className={`rounded-lg border p-2 ${
                entry.severity === 'critical' ? 'border-destructive/30' : 'border-border/40'
              }`}
            >
              <p className="font-medium">{stageLabel(entry.stage)}</p>
              <p className="mt-1 whitespace-pre-wrap">{entry.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});
DegradationNotice.displayName = 'DegradationNotice';

export default DegradationNotice;
